import type { ItemFieldEntry } from '@cheqroom/api';
import { type InfoField, KitInfo as SharedKitInfo, type KitInfoKit, type StackProps } from '@cheqroom/components';
import type { FC, PropsWithChildren } from 'react';

import type { UseKitFieldsHook } from '../../../hooks/kit-fields/use-kit-fields';
import { useKitFieldsConfig } from '../../../hooks/kit-fields/use-kit-fields-config';
import { useKitInfoFieldDefs } from '../../../hooks/kit-fields/use-kit-info-field-defs';

// Re-typed with this app's React 18 `FC` for the same cross-workspace reason as `ItemInfo` (see
// `ItemInfo/ItemInfo.tsx`). `KitInfoKit` and `InfoField` only flow in, so they are reused directly.

export type { KitInfoKit };

interface BaseProps extends StackProps {
	extraFields?: InfoField[];
}

/** The caller has the kit and decides which fields show. */
interface KitProps extends BaseProps {
	kit?: KitInfoKit;
	fieldDefs?: ItemFieldEntry[];
	isLoading?: boolean;
	kitId?: never;
}

/** Only the id: the kit comes from a batched fetch, and the fields are the user's inline-field choice. */
interface KitIdProps extends BaseProps {
	kitId: string;
	kitFields: UseKitFieldsHook;
	kit?: never;
	fieldDefs?: never;
	isLoading?: never;
}

export type Props = KitProps | KitIdProps;

export interface QRCodeProps {
	codes: string[];
}

export interface BarcodeProps {
	barcodes: string[];
}

interface ComponentComposition {
	Status: FC;
	Fields: FC;
	QRCode: FC<QRCodeProps>;
	Barcode: FC<BarcodeProps>;
}

const TypedKitInfo = SharedKitInfo as unknown as FC<PropsWithChildren<KitProps>> & ComponentComposition;

/** The configured inline fields for a kit held by a batched `useKitFields` fetch. */
const ConfiguredKitInfo: FC<PropsWithChildren<KitIdProps>> = ({ kitId, kitFields, children, ...rest }) => {
	const [fieldIds] = useKitFieldsConfig();
	const fieldDefs = useKitInfoFieldDefs(fieldIds);

	return (
		<TypedKitInfo
			{...rest}
			kit={(kitFields.getOriginalFields(kitId) as KitInfoKit | null) ?? undefined}
			fieldDefs={fieldDefs}
			isLoading={kitFields.isLoadingKit(kitId)}
		>
			{children}
		</TypedKitInfo>
	);
};

const WebKitInfo: FC<PropsWithChildren<Props>> = (props) =>
	props.kitId === undefined ? <TypedKitInfo {...props} /> : <ConfiguredKitInfo {...props} />;

// A new object, so the shared component (mobile uses it too) is not mutated.
export const KitInfo: FC<PropsWithChildren<Props>> & ComponentComposition = Object.assign(WebKitInfo, {
	Status: TypedKitInfo.Status,
	Fields: TypedKitInfo.Fields,
	QRCode: TypedKitInfo.QRCode,
	Barcode: TypedKitInfo.Barcode,
});
